import { createRxDatabase, addRxPlugin, RxCollection, RxDocument } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { QueryClient } from '@tanstack/react-query';

import {
  CustomerVisitDbSchema,
  ModifiedDateSchema,
  PendingBlobsSchema,
  ProductSchema,
  SurveyResponseSchema,
  SurveySchema,
  UserSchema
} from 'schemas';

import { User } from 'types/user';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { CustomerVisit } from 'types/customer';
import { Product } from 'types/product';
import { ModifiedDate } from 'types/date';
import { PendingBlob } from 'types/pendingBlob';

import { Database, DatabaseCollections, ObjectStores, TableValueType } from './types';
import {
  customerVisitsMethods,
  modifiedDatesMethods,
  pendingBlobMethods,
  productMethods,
  submittedSurveysMethods,
  unsubmittedSurveysMethods,
  userMethods
} from './ormMethods';
import { initReplication } from './replication';

let dbName = '';

export const setDbName = (uid: string) => (dbName = 'voiceOfTheCustomer-v2-' + uid);

let database: Database;
let creatingDatabasePromise: Promise<Database>;

// Helpers for migration v6 to avoid deep nesting and inline callbacks
const PAGE_KEY_CANDIDATES = ['page', 'Page', 'pageId', 'PageId'] as const;
type Mutable = { [key: string]: any };

function normalizePageItemInPlace(item: Mutable): void {
  if (!item || typeof item !== 'object') return;

  if (!('page' in item)) {
    for (const k of PAGE_KEY_CANDIDATES) {
      if (k in item && typeof item[k] === 'string') {
        item.page = item[k];
        delete item[k];
        break;
      }
    }
  }

  const qs = item.questions;
  if (Array.isArray(qs)) {
    // already ok
  } else if (qs == null) {
    item.questions = [];
  } else {
    item.questions = [qs];
  }
}

function isValidPageItem(item: any): boolean {
  return !!(item && typeof item.page === 'string');
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object';
}

function hasSubsectionsArray(v: unknown): v is { subsections: any[] } {
  return isRecord(v) && Array.isArray((v as any).subsections);
}

function processSubsections(subsections: Mutable[]): void {
  for (const ss of subsections) {
    if (ss && Array.isArray(ss.pages)) {
      for (const p of ss.pages) {
        normalizePageItemInPlace(p);
      }
      ss.pages = ss.pages.filter(isValidPageItem);
    }
  }
}

function pushObjectChildren(obj: Record<string, unknown>, stack: unknown[]): void {
  for (const key of Object.keys(obj)) {
    stack.push(obj[key]);
  }
}

function normalizeSubsectionPagesStructure(root: unknown): void {
  const stack: unknown[] = [root];
  while (stack.length) {
    const current = stack.pop();
    if (!isRecord(current)) continue;
    if (Array.isArray(current)) {
      for (const el of current) stack.push(el);
      continue;
    }
    if (hasSubsectionsArray(current)) {
      processSubsections((current as any).subsections);
    }
    pushObjectChildren(current, stack);
  }
}

const addCollections = async (database: Database) => {
  await database.addCollections({
    [ObjectStores.User]: {
      schema: UserSchema,
      methods: userMethods(database)
    },
    [ObjectStores.UnsubmittedSurveys]: {
      schema: SurveySchema as any, // problems with oneOf declaration
      methods: unsubmittedSurveysMethods(database),
      migrationStrategies: {
        1: (survey) => survey, // no need to change any existing surveys
        2: (survey) => survey, // change adds new field, doesn't alter existing ones
        3: (survey) => survey, // change adds new field, doesn't alter existing ones
        4: (survey) => survey,
        5: (survey) => {
          // Recursively normalize dependsOn fields to array if needed
          function normalizeDependsOn(obj: any) {
            if (!obj || typeof obj !== 'object') return;
            for (const key in obj) {
              if (key === 'dependsOn' && obj[key]) {
                if (!Array.isArray(obj[key]) && typeof obj[key] === 'object' && obj[key] !== null) {
                  obj[key] = [obj[key]];
                }
              }
              if (typeof obj[key] === 'object' && obj[key] !== null) {
                normalizeDependsOn(obj[key]);
              }
            }
          }

          normalizeDependsOn(survey);
          survey.version = 5;

          return survey;
        },
        6: (survey) => {
          // Normalize subsection pages items to have { page: string, questions: Question[] }
          normalizeSubsectionPagesStructure(survey);
          survey.version = 6;

          return survey;
        },
        7: (survey) => survey,
        8: (survey) => survey,
      },
      // this was necessary to explicitly define, otherwise replication didn't correctly update
      // survey definitions changed in the database
      conflictHandler: {
        isEqual: (a, b) => a._ts === b._ts,
        resolve: (i) => i.realMasterState
      }
    },
    [ObjectStores.SubmittedSurveys]: {
      schema: SurveyResponseSchema,
      methods: submittedSurveysMethods(database),
      migrationStrategies: {
        1: (surveyResponse) => surveyResponse, // change adds new field, doesn't alter existing ones
        2: (surveyResponse) => surveyResponse, // change adds new field, doesn't alter existing ones
        3: (surveyResponse) => surveyResponse // change adds new field, doesn't alter existing ones
      }
    },
    [ObjectStores.CustomerVisits]: {
      schema: CustomerVisitDbSchema,
      methods: customerVisitsMethods(database),
      migrationStrategies: {
        1: (oldCustomerVisits: any) => {
          oldCustomerVisits.customerVisits = oldCustomerVisits.customerVisits.map((cv: any) => {
            cv.plannedVisit.attendees = cv.plannedVisit.attendeeds;
            delete cv.plannedVisit.attendeeds;
          });
          return oldCustomerVisits;
        }
      }
    },
    [ObjectStores.Products]: {
      schema: ProductSchema,
      methods: productMethods(database)
    },
    [ObjectStores.ModifiedDates]: {
      schema: ModifiedDateSchema,
      methods: modifiedDatesMethods(database)
    },
    [ObjectStores.PendingBlobs]: {
      schema: PendingBlobsSchema,
      methods: pendingBlobMethods(database)
    }
  });

  return database;
};

export const createDb = async (): Promise<Database> => {
  if (!dbName.length) {
    throw new Error('Attempted to access database with user ID not set.');
  }

  try {
    if (!database && !creatingDatabasePromise) {
      const inDevMode = process.env.NODE_ENV === 'development';

      const getStorage = async () => {
        const storage = getRxStorageDexie({});

        if (inDevMode) {
          // Schema validator for development mode
          const { getAjv, wrappedValidateAjvStorage } = await import('rxdb/plugins/validate-ajv');

          try {
            // Configure AJV
            const ajv = getAjv();
            ajv.opts.allowUnionTypes = true;
            // addFormats(ajv) is unnecessary, already done in getAjv() in RxDB plugin 
          } catch (e: unknown) {
            console.warn(
              `Ajv configuration failed (possible failure in Hot Module Replacement, this happens usually in development mode, current NODE_ENV is ${process.env.NODE_ENV})`, e
            );
          }

          return wrappedValidateAjvStorage({ storage });
        }

        return storage;
      };

      if (inDevMode) {
        // Add dev mode plugin
        await import('rxdb/plugins/dev-mode').then((module) => {
          // turn off schema validation hook from the plugin
          // otherwise really difficult to get around having a _ts property (coming from Cosmos DB)
          // as the validation doesn't allow properties to begin with _
          delete module.RxDBDevModePlugin.hooks?.preCreateRxSchema?.after;
          addRxPlugin(module.RxDBDevModePlugin);
        });
      }

      // Add the plugins for cleanup and migration
      addRxPlugin(RxDBCleanupPlugin);
      addRxPlugin(RxDBMigrationSchemaPlugin);

      const db = await createRxDatabase<DatabaseCollections>({
        name: dbName,
        storage: await getStorage(),
        ignoreDuplicate: inDevMode,
        cleanupPolicy: {
          minimumDeletedTime: 10000 // already after 10 s, we don't need soft-deleted entities
        }
      });
      await addCollections(db);
      database = db;

      return database;
    }
  } catch (e: unknown) {
    console.warn(
      `createDb() failed (possible failure in Hot Module Replacement, this happens usually in development mode, current NODE_ENV is ${process.env.NODE_ENV})`, e
    );
  }

  if (!database) {
    return await creatingDatabasePromise;
  }

  return database;
};

const getDb = async () => {
  const db = database;

  if (!db) {
    creatingDatabasePromise = createDb();
    return creatingDatabasePromise;
  }

  return db;
};

export const add = async <T extends TableValueType>(table: ObjectStores, value: T) => {
  const db: Database = await getDb();
  const collection = db[table] as unknown as RxCollection<T>;
  await collection.insert(value);
};

export const put = async <T extends TableValueType>(table: ObjectStores, value: T, key: string) => {
  const db: Database = await getDb();
  const collection = db[table] as unknown as RxCollection<T>;
  const doc = await collection?.findOne(key).exec();

  if (doc) {
    await doc.update({ $set: value as any });
  } else {
    await collection.insert(value);
  }
};

export const getAll = async <T extends TableValueType>(table: ObjectStores): Promise<T[]> => {
  const db: Database = await getDb();
  const collection = db[table] as unknown as RxCollection<T>;
  const docs = await collection.find().exec();

  return docs.map((doc) => doc.toJSON() as T);
};

export const get = async <T extends TableValueType>(table: ObjectStores, key: string): Promise<T | null> => {
  const db: Database = await getDb();
  const collection = db[table] as unknown as RxCollection<T>;
  const doc = await collection.findOne(key).exec();

  return doc ? (doc.toJSON() as T) : null;
};

export const removeAll = async (table: ObjectStores) => {
  const db = await getDb();
  const collection = db[table];

  await collection?.remove();
};

export const remove = async (table: ObjectStores, key: string) => {
  const db = await getDb();
  const collection = db[table];
  const doc = await collection?.findOne(key).exec();

  if (doc) {
    await (doc as RxDocument<any>).remove();
  }
};

export const getUser = async (): Promise<User | null> => {
  const db = await getDb();
  return await userMethods(db).getUser();
};

export const upsertUser = async (user: User) => {
  const db = await getDb();
  return await userMethods(db).upsertUser(user);
};

export const deleteUser = async (userId: string) => {
  const db = await getDb();
  return await userMethods(db).deleteUser(userId);
};

export const purgeUsers = async () => {
  const db = await getDb();
  return await userMethods(db).purgeUsers();
};

export const upsertSurveys = async (surveys: Survey[]) => {
  const db = await getDb();
  return await unsubmittedSurveysMethods(db).upsertSurveys(surveys);
};

export const getSurveys = async (): Promise<Survey[]> => {
  const db = await getDb();
  return await unsubmittedSurveysMethods(db).getSurveys();
};

export const getSurveyByDefinitionId = async (surveyDefinitionId: string): Promise<Survey | null> => {
  const db = await getDb();
  return await unsubmittedSurveysMethods(db).getSurveyByDefinitionId(surveyDefinitionId);
};

export const upsertSurveyResponses = async (surveyResponses: SurveyResponse[]) => {
  const db = await getDb();
  return await submittedSurveysMethods(db).upsertSurveyResponses(surveyResponses);
};

export const getSurveyResponses = async (): Promise<SurveyResponse[]> => {
  const db = await getDb();
  return await submittedSurveysMethods(db).getSurveyResponses();
};

export const getSurveyResponseById = async (responseId: string): Promise<SurveyResponse | null> => {
  const db = await getDb();
  return await submittedSurveysMethods(db).getSurveyResponseById(responseId);
};

export const deleteSurveyResponse = async (responseId: string): Promise<boolean> => {
  const db = await getDb();
  return await submittedSurveysMethods(db).deleteSurveyResponse(responseId);
};

export const upsertCustomerVisits = async (customerVisits: CustomerVisit[]) => {
  const db = await getDb();
  return await customerVisitsMethods(db).upsertCustomerVisits(customerVisits);
};

export const getCustomerVisits = async (userId: string): Promise<CustomerVisit[]> => {
  const db = await getDb();
  return await customerVisitsMethods(db).getCustomerVisits(userId);
};

export const upsertProducts = async (products: Product[]) => {
  const db = await getDb();
  return await productMethods(db).upsertProducts(products);
};

export const getProducts = async (): Promise<Product[]> => {
  const db = await getDb();
  return await productMethods(db).getProducts();
};

export const getProductById = async (productId: string): Promise<Product | null> => {
  const db = await getDb();
  return await productMethods(db).getProductById(productId);
};

export const upsertModifiedDates = async (modifiedDates: ModifiedDate[]) => {
  const db = await getDb();
  return await modifiedDatesMethods(db).upsertModifiedDates(modifiedDates);
};

export const getPendingBlobByName = async (name: string) => {
  const db = await getDb();
  return await pendingBlobMethods(db).getPendingBlobByName(name);
};

export const upsertBlob = async (pendingBlob: PendingBlob) => {
  const db = await getDb();
  return await pendingBlobMethods(db).upsertBlob(pendingBlob);
};

export const deletePendingBlob = async (name: string) => {
  const db = await getDb();
  return await pendingBlobMethods(db).deletePendingBlob(name);
};

export const initDb = async (queryClient: QueryClient) => initReplication(await getDb(), queryClient);

export default {
  add,
  get,
  getAll,
  put,
  remove,
  removeAll,
  getUser,
  upsertUser,
  upsertSurveys,
  getSurveys,
  upsertSurveyResponses,
  getSurveyResponses,
  getSurveyResponseById,
  deleteSurveyResponse,
  upsertCustomerVisits,
  getCustomerVisits,
  upsertProducts,
  getProducts,
  getProductById,
  upsertModifiedDates,
  getPendingBlobByName,
  upsertBlob,
  deletePendingBlob,
  initDb
};

export { ObjectStores };
export type { TableValueType };
